import { ItemBar } from "components/ItemBar";
import { ToastContext } from "context/ToastContext";
import { prepareCodesForSubmit, RangeTooLargeError } from "helpers/codeRanges";
import { Button } from "primereact/button";
import { useContext, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useCreateCaseRateRulesetMutation } from "store/queries/pricing";

import {
  mapCaseRateFormToRequestBody,
  getRulesetList,
} from "../helpers/mapFormToRequestBody";
import { useCaseRateCptConflicts } from "../hooks/useCaseRateCptConflicts";

function extractServerErrorMessage(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, message } = error as any;
  if (typeof data === "string") return data;
  if (data && typeof data === "object") {
    if (typeof data.message === "string") return data.message;
    if (typeof data.error === "string") return data.error;
  }
  return typeof message === "string" ? message : null;
}

export function SubmitCaseRate() {
  const {
    getValues,
    formState: { isValid },
  } = useFormContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [useCreateCaseRateRuleset] = useCreateCaseRateRulesetMutation();
  const { hasConflicts } = useCaseRateCptConflicts();

  const { current: toastElement } = useContext(ToastContext);

  const createPayerRule = async () => {
    const values = getValues();
    let cptCodes: string[];
    let excludedCptCodes: string[];
    try {
      cptCodes = prepareCodesForSubmit(values.cptCodes ?? []);
      excludedCptCodes = prepareCodesForSubmit(values.excludedCptCodes ?? []);
    } catch (err) {
      toastElement?.show({
        severity: "error",
        summary: "Range too large",
        detail:
          err instanceof RangeTooLargeError
            ? err.message
            : "Failed to expand code ranges",
      });
      return;
    }
    const rulesToCreateRaw = getRulesetList(
      { ...values, cptCodes, excludedCptCodes },
      false
    );
    const rulesToCreate = rulesToCreateRaw.map((rule) => {
      // CLE-2524: creates declare applicability explicitly (wins over isDefault).
      return {
        ...mapCaseRateFormToRequestBody(rule),
        applicability: rule.isDefault
          ? ("TYPICAL" as const)
          : ("SCOPED" as const),
      };
    });
    setLoading(true);
    try {
      const results = await Promise.all(
        rulesToCreate.map((rule) =>
          useCreateCaseRateRuleset(rule)
            .unwrap()
            .then(() => ({ ok: true as const }))
            .catch((error: unknown) => ({
              ok: false as const,
              message: extractServerErrorMessage(error),
            }))
        )
      );
      const failed = results.filter((r) => !r.ok);
      if (failed.length === 0) {
        toastElement?.show({
          severity: "success",
          summary: "Payer Ruleset(s) created",
          detail: "Payer Ruleset(s) created successfully",
        });
        setLoading(false);
        navigate("/payer-rules");
      } else {
        const messages = Array.from(
          new Set(
            failed
              .map((r) => (r.ok ? null : r.message))
              .filter((m): m is string => !!m)
          )
        );
        toastElement?.show({
          severity: "error",
          summary: "Error",
          detail:
            messages.length > 0
              ? messages.join("\n")
              : "An error has occurred attempting to create one or more discount rulesets",
        });
        setLoading(false);
        if (messages.length === 0) {
          navigate("/payer-rules");
        }
      }
    } catch (error) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "An error has occurred",
      });
      setLoading(false);
    }
  };
  return (
    <ItemBar>
      <div
        data-testid="submit__Hospital"
        className="flex flex-1 justify-content-end"
      >
        <Button
          label="Create Payer Rule"
          onClick={createPayerRule}
          disabled={!isValid || hasConflicts}
          loading={loading}
        />
      </div>
    </ItemBar>
  );
}
